import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Heart, Sparkles, Play } from "lucide-react";
import { api } from "@/lib/api";
import ShareStoryMenu from "@/components/umaang/ShareStoryMenu";

/**
 * Success Stories Carousel — landing-page module.
 * Fetches approved stories from GET /success-stories, auto-rotates every 6s,
 * shows 1 story on mobile / 2 on tablet / 3 on desktop.
 */
export default function SuccessStoriesCarousel() {
  const [stories, setStories] = useState([]);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);
  const [playing, setPlaying] = useState(null); // story id currently playing inline

  useEffect(() => {
    api.get("/success-stories", { params: { limit: 12 } })
      .then(({ data }) => setStories(data.stories || []))
      .catch(() => setStories([]));
  }, []);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setPerView(w < 640 ? 1 : w < 1024 ? 2 : 3);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const total = stories.length;
  const maxIndex = Math.max(0, total - perView);

  const next = useCallback(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);
  const prev = useCallback(() => setIndex((i) => (i <= 0 ? maxIndex : i - 1)), [maxIndex]);

  // Autoplay every 6s — paused while a video is playing
  useEffect(() => {
    if (total <= perView) return undefined;
    if (playing) return undefined;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, total, perView, playing]);

  if (!stories.length) return null;

  const slidePct = 100 / perView;

  return (
    <section
      className="border-t border-[#D6D2C9] relative"
      style={{ background: "linear-gradient(180deg, #FFFDF9 0%, #FAF7F2 100%)" }}
      data-testid="success-stories-section"
    >
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] to-[#DB2777] bg-clip-text text-transparent font-semibold flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#C85A53]" strokeWidth={2} /> Success stories
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 max-w-2xl">
              Real couples. Real rishtas.
            </h2>
            <p className="mt-2 text-sm text-[#57534E] max-w-xl">
              10,000+ families have found their forever on Umaang. Here are just a few of their journeys.
            </p>
          </div>

          {total > perView && (
            <div className="flex gap-2">
              <button
                data-testid="stories-prev-btn"
                onClick={prev}
                aria-label="Previous story"
                className="w-10 h-10 rounded-full grid place-items-center border border-[#D6D2C9] bg-white hover:bg-[#FAF7F2] transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="stories-next-btn"
                onClick={next}
                aria-label="Next story"
                className="w-10 h-10 rounded-full grid place-items-center border border-[#D6D2C9] bg-white hover:bg-[#FAF7F2] transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="mt-10 overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${index * slidePct}%)` }}
          >
            {stories.map((s, i) => (
              <article
                key={s.id || i}
                data-testid={`story-card-${i}`}
                className="shrink-0 px-3"
                style={{ width: `${slidePct}%` }}
              >
                <div className="h-full bg-white border border-[#D6D2C9] shadow-[0_10px_35px_rgba(0,0,0,0.06)] overflow-hidden card-hover relative" style={{ borderRadius: 16 }}>
                  {/* Floating share button — top-right */}
                  <ShareStoryMenu story={s} className="absolute top-3 right-3 z-20" />
                  {s.youtube_id ? (
                    playing === s.id ? (
                      <div className="aspect-video bg-black">
                        <iframe
                          title={s.couple_names}
                          src={`${s.youtube_embed_url}?autoplay=1&rel=0&modestbranding=1`}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <button
                        data-testid={`story-play-${i}`}
                        onClick={() => setPlaying(s.id)}
                        className="relative w-full aspect-video overflow-hidden bg-black group"
                        aria-label={`Play ${s.couple_names} video story`}
                      >
                        <img
                          src={s.youtube_thumbnail}
                          alt={`${s.couple_names} video story`}
                          loading="lazy"
                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
                        <div className="absolute inset-0 grid place-items-center">
                          <div className="w-16 h-16 rounded-full bg-white/95 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                            <Play className="w-7 h-7 text-[#C85A53] ml-1" fill="#C85A53" />
                          </div>
                        </div>
                        <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-red-600 text-white px-2 py-1 rounded-full font-semibold">
                          Video story
                        </div>
                      </button>
                    )
                  ) : s.photo_url ? (
                    <div className="aspect-[4/3] overflow-hidden bg-[#FAF7F2]">
                      <img
                        src={s.photo_url}
                        alt={s.couple_names}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  ) : null}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-[#C85A53]">
                      <Heart className="w-4 h-4" fill="#C85A53" />
                      <div className="font-display text-xl">{s.couple_names}</div>
                    </div>
                    {(s.city || s.wedding_date) && (
                      <div className="text-xs text-[#57534E] uppercase tracking-widest mt-2">
                        {[s.city, s.wedding_date && new Date(s.wedding_date).toLocaleDateString("en-IN", { month: "long", year: "numeric" })].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <p className="mt-4 text-sm text-[#3C3936] leading-relaxed line-clamp-6">
                      {s.story}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {total > perView && (
          <div className="mt-8 flex justify-center gap-1.5">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={`dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`stories-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[#C85A53]" : "w-1.5 bg-[#D6D2C9]"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
